import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import { useAuth } from 'context/AuthContext';
import i18n from '../../../i18n';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const profileRef = useRef(null);
  const [language, setLanguage] = useState('en');

  const { user, logout } = useAuth();

  const navigationItems = [
    {
      label: 'Dashboard',
      path: '/chapter-overview-dashboard',
      icon: 'LayoutDashboard',
      description: 'Chapter overview and learning hub'
    },
    {
      label: 'Learn',
      path: '/interactive-presentation-viewer',
      icon: 'BookOpen',
      description: 'Interactive presentations and content'
    },
    {
      label: 'Quiz',
      path: '/quiz-assessment-interface',
      icon: 'Brain',
      description: 'Knowledge assessments and tests'
    },
    {
      label: 'Progress',
      path: '/learning-progress-tracker',
      icon: 'TrendingUp',
      description: 'Learning progress and achievements'
    }
  ];

  const isActivePath = (path) => location.pathname === path;

  const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);
  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  const toggleProfileMenu = () => setIsProfileMenuOpen((prev) => !prev);
  const closeProfileMenu = () => setIsProfileMenuOpen(false);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        closeProfileMenu();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);


  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'es' : 'en';
    setLanguage(newLang);

    i18n.changeLanguage(newLang);
  };
  return (
    <header className="fixed top-0 left-0 right-0 border-b z-100 bg-surface border-border">
      <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link
            to="/chapter-overview-dashboard"
            className="flex items-center p-2 -m-2 space-x-3 rounded-lg hover-scale focus-ring"
            onClick={closeMobileMenu}
          >
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-primary to-secondary">
              <Icon name="Zap" size={20} color="white" strokeWidth={2.5} />
            </div>
            <div className="flex flex-col">
              <span className="text-lg font-bold font-heading text-text-primary">
                AI Learning Hub
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="items-center hidden space-x-1 md:flex">
            {navigationItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`
                  flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium
                  transition-all duration-150 ease-out min-touch-target
                  ${isActivePath(item.path)
                    ? 'bg-primary text-primary-foreground shadow-md'
                    : 'text-text-secondary hover:text-text-primary hover:bg-surface-secondary'
                  }
                  focus-ring
                `}
                title={item.description}
              >
                <Icon
                  name={item.icon}
                  size={18}
                  color="currentColor"
                  strokeWidth={isActivePath(item.path) ? 2.5 : 2}
                />
                <span>{item.label}</span>
              </Link>
            ))}
          </nav>

          {/* Right Controls */}
          <div className="flex items-center space-x-2 md:space-x-4">
            {/* Mobile Menu Button */}
            <div className="md:hidden">
              <Button
                variant="ghost"
                onClick={toggleMobileMenu}
                className="p-2"
                iconName={isMobileMenuOpen ? "X" : "Menu"}
                iconSize={24}
              >
                <span className="sr-only">
                  {isMobileMenuOpen ? 'Close menu' : 'Open menu'}
                </span>
              </Button>
            </div>

            {/* Profile Dropdown */}
            <div className="relative" ref={profileRef}>
              <Button
                variant="ghost"
                onClick={toggleProfileMenu}
                className="p-2"
                iconName="User"
                iconSize={20}
              >
              </Button>

              {isProfileMenuOpen && (
                <div className="absolute right-0 z-50 w-48 py-2 mt-2 border rounded-lg shadow-lg bg-surface border-border">
                  <div className="px-4 py-2 text-sm font-medium text-text-primary">
                    {user?.name || 'User'}
                  </div>
                  <button
                    onClick={() => {
                      logout();
                      closeProfileMenu();
                      navigate('/authentication-login'); // Optional redirect
                    }}
                    className="w-full px-4 py-2 text-sm text-left text-text-secondary hover:bg-surface-secondary hover:text-text-primary"
                  >
                    Logout
                  </button>
                </div>
              )}
            </div>

            <Button
              variant="ghost"
              onClick={toggleLanguage}
              className="p-2"
              iconName="Globe"
              iconSize={20}
            >
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      {isMobileMenuOpen && (
        <div className="border-t shadow-lg md:hidden bg-surface border-border">
          <nav className="px-4 py-3 space-y-1">
            {navigationItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={closeMobileMenu}
                className={`
                  flex items-center space-x-3 px-4 py-3 rounded-lg text-base font-medium
                  transition-all duration-150 ease-out min-touch-target
                  ${isActivePath(item.path)
                    ? 'bg-primary text-primary-foreground shadow-md'
                    : 'text-text-secondary hover:text-text-primary hover:bg-surface-secondary'
                  }
                  focus-ring
                `}
              >
                <Icon
                  name={item.icon}
                  size={20}
                  color="currentColor"
                  strokeWidth={isActivePath(item.path) ? 2.5 : 2}
                />
                <div className="flex flex-col">
                  <span>{item.label}</span>
                  <span className="text-xs text-text-muted mt-0.5">
                    {item.description}
                  </span>
                </div>
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
